import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiLoader,
  FiMail,
  FiLock,
  FiCalendar,
  FiAlertCircle,
  FiUsers,
  FiCheckSquare,
  FiBook
} from 'react-icons/fi';
import './Login.css';

const Login = () => {
  const { login, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  const [formData, setFormData] = useState({ email: '', senha: '', rememberMe: false });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState('');

  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  if (loading) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <FiLoader className="spin" />
          <span>Verificando acesso…</span>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    if (generalError) setGeneralError('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email.trim()) errors.email = 'Email ou nome de usuário é obrigatório';
    if (!formData.senha) errors.senha = 'Senha é obrigatória';
    else if (formData.senha.length < 6) errors.senha = 'Senha deve ter pelo menos 6 caracteres';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setIsSubmitting(true);
    setFormErrors({});
    setGeneralError('');

    try {
      const result = await login(formData.email, formData.senha);
      if (!result.success) {
        setGeneralError(result.message || 'Credenciais inválidas. Verifique seus dados e tente novamente.');
      }
    } catch {
      setGeneralError('Erro de conexão. Verifique sua internet e tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">

      {/* ── PAINEL ESQUERDO — Branding ── */}
      <div className="login-panel-left">
        <div className="login-brand-inner">
          <div className="login-brand-logo">
            <FiCalendar />
          </div>
          <h1 className="login-brand-name">Sistema de Reuniões</h1>
          <p className="login-brand-desc">
            Gestão completa de reuniões, presenças, irmandade e recitativos em um só lugar.
          </p>

          <ul className="login-features">
            <li><FiUsers /><span>Controle de irmandade e colaboradores</span></li>
            <li><FiCheckSquare /><span>Registro de presenças e reuniões</span></li>
            <li><FiBook /><span>Recitativos com frequência automática</span></li>
          </ul>
        </div>

        <p className="login-brand-copy">© {new Date().getFullYear()} Sistema de Reuniões</p>
      </div>

      {/* ── PAINEL DIREITO — Formulário ── */}
      <div className="login-panel-right">
        <div className="login-form-box">

          {/* Logo visível só no mobile */}
          <div className="login-mobile-logo">
            <div className="login-mobile-icon"><FiCalendar /></div>
            <span>Sistema de Reuniões</span>
          </div>

          <h2 className="login-form-title">Bem-vindo de volta</h2>
          <p className="login-form-sub">Faça login para acessar o sistema</p>

          <form className="login-form" onSubmit={handleSubmit} noValidate>

            {generalError && (
              <div className="login-error-banner">
                <FiAlertCircle />
                <span>{generalError}</span>
              </div>
            )}

            {/* Email / usuário */}
            <div className="lf-group">
              <label htmlFor="email" className="lf-label">Email ou Nome de Usuário</label>
              <div className="lf-input-wrap">
                <FiMail className="lf-icon" />
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="seu@email.com ou nome.usuario"
                  className={`lf-input ${formErrors.email ? 'lf-input--error' : ''}`}
                  disabled={isSubmitting}
                  autoComplete="username"
                  autoFocus
                />
              </div>
              {formErrors.email && (
                <p className="lf-field-error"><FiAlertCircle />{formErrors.email}</p>
              )}
            </div>

            {/* Senha */}
            <div className="lf-group">
              <label htmlFor="senha" className="lf-label">Senha</label>
              <div className="lf-input-wrap">
                <FiLock className="lf-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="senha"
                  name="senha"
                  value={formData.senha}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                  className={`lf-input ${formErrors.senha ? 'lf-input--error' : ''}`}
                  disabled={isSubmitting}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="lf-eye"
                  onClick={() => setShowPassword(s => !s)}
                  disabled={isSubmitting}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {formErrors.senha && (
                <p className="lf-field-error"><FiAlertCircle />{formErrors.senha}</p>
              )}
            </div>

            {/* Lembrar + Esqueceu */}
            <div className="lf-row-remember">
              <label className="lf-check-label">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="lf-checkbox"
                  disabled={isSubmitting}
                />
                <span>Lembrar de mim</span>
              </label>
              <button
                type="button"
                className="lf-forgot"
                onClick={() => toast.info('Funcionalidade em desenvolvimento')}
              >
                Esqueceu a senha?
              </button>
            </div>

            <button type="submit" className="lf-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <><div className="lf-spinner" /> Entrando…</>
              ) : (
                <>Entrar no Sistema <FiArrowRight /></>
              )}
            </button>

          </form>
        </div>
      </div>

    </div>
  );
};

export default Login;
